/**
 * A tiny module-level bridge to the chart's charted-depth query.
 *
 * The vector cell index lives inside MapView (a React ref), but a few views that
 * sit outside that tree - like the open-water "What's here?" popup - want the
 * charted depth at an arbitrary tapped point, not just under the boat. MapView
 * registers a query closure here (its live ChartIndex plus the "does this
 * provider carry depth at all" check) on map creation and clears it on teardown,
 * so callers get an honest answer: a metres value, or null when there's no depth
 * on this source or nothing charted at the point.
 */
import type { ChartedDepthDetail } from '@/safety';

export type ChartedDepthQuery = (pos: [number, number]) => number | null;

let query: ChartedDepthQuery | null = null;

/** MapView registers (or clears, with null) the live charted-depth query. */
export function setChartedDepthQuery(fn: ChartedDepthQuery | null): void {
  query = fn;
}

/** Charted depth (metres to datum) at a position, or null when unavailable. */
export function chartedDepthAtNow(pos: [number, number]): number | null {
  return query ? query(pos) : null;
}

/**
 * The same bridge for the charted quality band (CATZOC / ZOC) at an arbitrary
 * point. Quality lives in the vector ENC's M_QUAL polygons, so like depth it is
 * only meaningful on a vector source; MapView registers a query over the live
 * cells and clears it on teardown. Callers get a ZOC band letter (e.g. 'B'), or
 * null when there's no quality data on this source or none charted at the point.
 */
export type ChartedZocQuery = (pos: [number, number]) => string | null;

let zocQuery: ChartedZocQuery | null = null;

/** MapView registers (or clears, with null) the live charted-quality query. */
export function setChartedZocQuery(fn: ChartedZocQuery | null): void {
  zocQuery = fn;
}

/** Charted quality band (CATZOC/ZOC letter) at a position, or null when unavailable. */
export function chartedZocAtNow(pos: [number, number]): string | null {
  return zocQuery ? zocQuery(pos) : null;
}

/**
 * The fuller depth picture at a point: the depth area it sits in and the nearest
 * sounding, kept apart rather than boiled down to one figure. The "What's here?"
 * readout uses this so it can say "0 to 18 m depth area, nearest sounding 12.4 m"
 * instead of the misleading "0.0 m" a single cautious number produces. Registered
 * and cleared by MapView alongside the plain depth query.
 */
export type ChartedDepthDetailQuery = (pos: [number, number]) => ChartedDepthDetail | null;

let detailQuery: ChartedDepthDetailQuery | null = null;

/** MapView registers (or clears, with null) the live depth-detail query. */
export function setChartedDepthDetailQuery(fn: ChartedDepthDetailQuery | null): void {
  detailQuery = fn;
}

/** The depth area and nearest sounding at a position, or null when unavailable. */
export function chartedDepthDetailAtNow(pos: [number, number]): ChartedDepthDetail | null {
  return detailQuery ? detailQuery(pos) : null;
}
